// Serializes dashboard state → the backend DashboardSpec shape. Extracted from
// DashboardContext; consumes buildQueriesForWidget for each widget's query array.

import type { ComponentSpec } from "../Processing/types";
import type { DashboardState } from "./dashboardState";
import { buildQueriesForWidget, type EligibleControllers } from "../Processing/buildQueries";
import { eligibleByKind, listControllers } from "../Processing/controllerBindings";

const TYPE_PREFIX_RE = /^(chart|table_2d|table|title|text|logo|stat_tile|player_notes|spacer|gauge|radar|pie_chart|player_card|body_map|ai_insights|slicer|scatterplot)-/;
export function stripTypePrefix(id: string): string {
  return id.replace(TYPE_PREFIX_RE, "");
}

function sanitizeConfig(
  spec: ComponentSpec | undefined,
  type: string,
  eligible?: EligibleControllers,
): Record<string, unknown> {
  if (!spec) return { type };
  const config = { ...spec } as Record<string, unknown>;
  // player_card has no queries, so its controller reference rides on the config. Drop it
  // when it no longer resolves — the backend rejects a dangling reference outright.
  if (type === "player_card") {
    const ref = config.singlePlayerControllerId as string | undefined;
    config.single_player_controller_id =
      ref && eligible?.single_player.has(ref) ? ref : undefined;
  }
  const display = config.display as Record<string, unknown> | undefined;
  if (display) {
    config.display = Object.fromEntries(
      Object.entries(display).map(([k, v]) => [k, v === null || v === undefined ? "" : v]),
    );
  }
  return config;
}

export function serializeDashboardSpec(state: DashboardState) {
  // A binding is stamped onto a saved query only when its controller still exists and
  // still offers that kind — the backend rejects a dangling reference outright.
  const controllers = listControllers(Object.values(state.widgets));
  const eligible: EligibleControllers = eligibleByKind(controllers);

  const widgets = Object.values(state.widgets).map((w) => ({
    id: stripTypePrefix(w.id),
    type: w.type,
    config: sanitizeConfig(w.spec, w.type, eligible),
    queries: buildQueriesForWidget(w.spec, eligible),
  }));

  const layout = state.layout.map((item) => ({
    i: stripTypePrefix(item.i),
    x: item.x,
    y: item.y,
    w: item.w,
    h: item.h,
  }));

  return {
    id: state.dashboardId,
    spec_version: state.specVersion,
    widgets,
    layout,
  };
}
